import { ScannedProduct, FALLBACK_PRODUCTS } from './purchaseData'

// Price regex — captures common currency formats: $27, $27.00, 27 USD, USD 27, etc.
const PRICE_REGEX =
  /(?:\$|USD\s*|€|EUR\s*)\s?(\d{1,5}(?:[.,]\d{2})?)|(\d{1,5}(?:[.,]\d{2})?)\s*(?:USD|EUR|€|\$)/i

const CTA_KEYWORDS = [
  'comprar',
  'añadir al carrito',
  'agregar al carrito',
  'lo necesito',
  'quiero',
  'obtener',
  'comenzar',
  'add to cart',
  'buy',
  'checkout',
  'order',
  'get it',
]

const EDITION_KEYWORDS = [
  'physical book',
  'digital edition',
  'edición digital',
  'pack despertar',
  'libro físico',
  'edición premium',
  'pago único',
]

function parsePrice(text: string): { number: number; text: string } | null {
  if (!text) return null
  const match = text.match(PRICE_REGEX)
  if (!match) return null
  const raw = (match[1] || match[2] || '').replace(',', '.')
  const num = Number(raw)
  if (!Number.isFinite(num) || num <= 0) return null
  return { number: num, text: match[0].trim() }
}

// Finds a strikethrough/original price within the container.
// Looks for <s>, <del>, or elements with line-through styling.
function findStrikethroughPrice(container: Element): string | undefined {
  const candidates = container.querySelectorAll<HTMLElement>(
    's, del, [class*="line-through"], [style*="line-through"]',
  )
  for (const c of candidates) {
    const text = (c.textContent || '').trim()
    const parsed = parsePrice(text)
    if (parsed) return parsed.text
  }
  return undefined
}

function getClosestText(el: Element, maxChars = 120): string {
  const t = (el.textContent || '').trim().replace(/\s+/g, ' ')
  return t.slice(0, maxChars)
}

function findNearestImage(el: Element): string | undefined {
  // Walk up and search siblings for an <img>
  let node: Element | null = el
  for (let i = 0; i < 6 && node; i++) {
    const img = node.querySelector?.('img') as HTMLImageElement | null
    if (img?.src) return img.src
    node = node.parentElement
  }
  return undefined
}

function findNearestHeading(el: Element): string | undefined {
  let node: Element | null = el
  for (let i = 0; i < 8 && node; i++) {
    const h = node.querySelector?.('h1, h2, h3, h4')
    if (h?.textContent) return h.textContent.trim().replace(/\s+/g, ' ')
    node = node.parentElement
  }
  return undefined
}

function findEditionLabel(el: Element): string | undefined {
  const text = getClosestText(el, 600).toLowerCase()
  for (const kw of EDITION_KEYWORDS) {
    if (text.includes(kw)) {
      // Title-case the matched keyword
      return kw
        .split(' ')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ')
    }
  }
  return undefined
}

// 1) Try JSON-LD Product / Offer
function scanJsonLd(): ScannedProduct[] {
  const out: ScannedProduct[] = []
  const scripts = document.querySelectorAll<HTMLScriptElement>(
    'script[type="application/ld+json"]',
  )
  scripts.forEach((s) => {
    try {
      const raw = s.textContent
      if (!raw) return
      const data = JSON.parse(raw)
      const items = Array.isArray(data) ? data : [data]
      for (const item of items) {
        if (!item || typeof item !== 'object') continue
        const type = item['@type']
        if (
          type === 'Product' ||
          (Array.isArray(type) && type.includes('Product'))
        ) {
          const offers = item.offers
            ? Array.isArray(item.offers)
              ? item.offers
              : [item.offers]
            : []
          const offer = offers[0]
          const priceNum = offer ? Number(offer.price) : NaN
          if (item.name && Number.isFinite(priceNum) && priceNum > 0) {
            const currency = offer?.priceCurrency || 'USD'
            out.push({
              title: String(item.name),
              priceNumber: priceNum,
              priceText: `$${priceNum} ${currency}`,
              thumbnailUrl: Array.isArray(item.image)
                ? item.image[0]
                : item.image,
            })
          }
        }
      }
    } catch {
      // ignore malformed JSON-LD
    }
  })
  return out
}

// 2) Element-based scan: look for CTAs, walk up to find product container
function scanCTAs(): ScannedProduct[] {
  const out: ScannedProduct[] = []
  const seen = new Set<string>()

  const candidates = Array.from(
    document.querySelectorAll<HTMLElement>('a, button'),
  ).filter((el) => {
    const t = (el.textContent || '').toLowerCase()
    return CTA_KEYWORDS.some((kw) => t.includes(kw))
  })

  for (const cta of candidates) {
    // Walk up a few levels to find a section-like container
    let container: HTMLElement | null = cta
    for (let i = 0; i < 5 && container; i++) {
      if (
        container.tagName === 'SECTION' ||
        container.tagName === 'ARTICLE' ||
        container.classList.contains('product') ||
        container.children.length > 3
      ) {
        break
      }
      container = container.parentElement
    }
    if (!container) continue

    const containerText = getClosestText(container, 1500)
    const price = parsePrice(containerText)
    if (!price) continue

    const title = findNearestHeading(container) || 'Producto'
    const thumbnailUrl = findNearestImage(container)
    const editionLabel = findEditionLabel(container)
    const originalPriceText = findStrikethroughPrice(container)

    const key = `${title}::${price.number}`
    if (seen.has(key)) continue
    seen.add(key)

    out.push({
      title,
      editionLabel,
      priceNumber: price.number,
      priceText: price.text,
      originalPriceText,
      thumbnailUrl,
    })
  }

  return out
}

export function scanProducts(): ScannedProduct[] {
  let products: ScannedProduct[] = []
  try {
    products = scanJsonLd()
    if (products.length === 0) {
      products = scanCTAs()
    }
  } catch {
    // swallow — fall back below
  }
  if (products.length === 0) {
    return FALLBACK_PRODUCTS
  }
  return products
}
